import React, { useMemo, memo } from "react";
import useCustomQuery from "../../Hooks/useCustomQuery";
import useCustomFecthQuery from "../../Hooks/useCustomFecthQuery";
import { useParams, useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import LoaderComingSoon from "../../components/Loader/LoaderComingSoon";
import PublicEventOne from "../../components/Common/EventLanding/WebinarLandingPage";
import CustomLandingPage from "../../components/Common/EventLanding/CustomLandingPage";
import CustomFooter from "../../Layout/HorizontalLayout/CustomFooter";
import Loading from "../../components/Loader/Loading";

// Memoized wrapper component to optimize data transformations
const CustomLandingPageWithMemo = memo(({ landingPage, getMyEventDetail, ticketSettingsData, sessionsData, registrationFormData, eventID, params }) => {
  // Memoize expensive data transformations
  const transformedTickets = useMemo(() => {
    if (!ticketSettingsData?.data) return [];
    const today = new Date();
    return ticketSettingsData.data.map(ticket => {
      const isEarlyBirdActive = ticket.allowEarlyBird &&
        ticket.earlyBirdStartDate &&
        ticket.earlyBirdEndDate &&
        today >= new Date(ticket.earlyBirdStartDate) &&
        today <= new Date(ticket.earlyBirdEndDate);
      
      const ticketPrice = isEarlyBirdActive ? (ticket.tierEarlyBirdPrice || ticket.price) : (ticket.price || 0);
      
      return {
        name: ticket.tier || ticket.name || 'Ticket',
        title: ticket.tier || ticket.name || 'Ticket',
        description: ticket.description || ticket.ticketTerms || '',
        price: ticketPrice,
        features: ticket.features || [],
        id: ticket._id || ticket.id,
        isEarlyBird: isEarlyBirdActive
      };
    });
  }, [ticketSettingsData?.data]);

  const transformedSessions = useMemo(() => {
    if (!sessionsData?.data) return [];
    return sessionsData.data.map(session => ({
      title: session.sessionName || session.name || session.title,
      name: session.sessionName || session.name || session.title,
      time: session.sessionTime || session.time || '',
      date: session.sessionDate || session.date || '',
      startTime: session.startTime || '',
      endTime: session.endTime || '',
      description: session.description || session.sessionDescription || '',
      location: session.location || session.sessionLocation || '',
      sessionID: session._id || session.sessionID || session.id,
      _id: session._id || session.id
    }));
  }, [sessionsData?.data]);

  const colorSchemeValue = useMemo(() => {
    if (landingPage?.colorSchemeIndex !== undefined && landingPage?.colorSchemeIndex !== null) {
      const schemes = [
        { id: "vibrant", colors: ["#f97316", "#fb923c", "#fdba74", "#fed7aa"] },
        { id: "professional", colors: ["#3b82f6", "#60a5fa", "#93c5fd", "#dbeafe"] },
        { id: "elegant", colors: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#e9d5ff"] },
        { id: "nature", colors: ["#22c55e", "#4ade80", "#86efac", "#bbf7d0"] },
        { id: "sunset", colors: ["#f72585", "#ff006e", "#fb8500", "#ffb703"] },
      ];
      const index = typeof landingPage.colorSchemeIndex === 'number' 
        ? landingPage.colorSchemeIndex 
        : parseInt(landingPage.colorSchemeIndex, 10);
      return (!isNaN(index) && index >= 0 && index < schemes.length) ? schemes[index] : schemes[0];
    }
    return landingPage?.colorScheme;
  }, [landingPage?.colorSchemeIndex, landingPage?.colorScheme]);

  return (
    <CustomLandingPage
      eventData={{
        ...(landingPage.eventData || {}),
        // Ensure logo is available even if not stored in landing page config
        logoImage: landingPage?.eventData?.logoImage || getMyEventDetail?.data?.logo || '',
        faq: landingPage?.eventData?.faq || getMyEventDetail?.data?.faq || [],
        tickets: transformedTickets,
        schedule: transformedSessions,
        supportMail: landingPage?.eventData?.supportMail || getMyEventDetail?.data?.supportMail || '',
        supportPhoneNumber: landingPage?.eventData?.supportPhoneNumber || getMyEventDetail?.data?.supportPhoneNumber || '',
        // Event format to determine in-person vs virtual behaviour on the landing page
        eventFormat: landingPage?.eventData?.eventFormat || getMyEventDetail?.data?.eventFormat || '',
        endDate: landingPage?.eventData?.endDate || getMyEventDetail?.data?.endDate || '',
        endTime: landingPage?.eventData?.endTime || getMyEventDetail?.data?.endTime || ''
      }}
      colorScheme={colorSchemeValue}
      templateId={landingPage?.templateId}
      registrationForm={registrationFormData?.data?.[0]?.customFields || []}
      eventID={eventID}
      eventName={landingPage?.eventData?.eventName || landingPage?.eventData?.name || params?.eventName}
    />
  );
});

const EventLandingCustomPublic = () => {
  document.title = `Kago Events`;
  
  // Safely get token from localStorage
  let token = null;
  try {
    const authData = localStorage.getItem("authEventKagoUser");
    if (authData) {
      const obj = JSON.parse(authData);
      token = obj?.data?.token;
    }
  } catch (error) {
    // Silently fail if localStorage is not available or data is corrupted
    console.error("Error reading auth data:", error);
  }
  
  const params = useParams();
  const [searchParams] = useSearchParams();
  const eventID = params.eventID;
  const isPreview = searchParams.get("preview") === "true";

 const {
    data: getMyEventDetail,
    error: errorGetMyEventDetail,
    isLoading: isLoadingGetMyEventDetail,
  } = useCustomQuery(
    ["myEventDetail", eventID],
    `${process.env.REACT_APP_API}guest/events/${eventID}`,
    null
  );  

  // Fetch custom landing page data
  const {
    data: landingPageData,
    isLoading: isLoadingLandingPage,
    error: landingPageError,
  } = useCustomFecthQuery(
    ["eventLandingPage", eventID],
    `${process.env.REACT_APP_API}event-landing-pages/${eventID}/event`,
    token,
    { enabled: !!eventID }
  );

  // Fetch ticket settings - Non-blocking (loads in background, deferred)
  const {
    data: ticketSettingsData,
    isLoading: isLoadingTickets,
  } = useCustomFecthQuery(
    ["ticketSettings", eventID],
    `${process.env.REACT_APP_API}ticket-settings/${eventID}/event`,
    token,
    { 
      enabled: !!eventID && !!landingPageData,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Fetch sessions - Non-blocking (loads in background, deferred)
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
  } = useCustomFecthQuery(
    ["guestSessions", eventID],
    `${process.env.REACT_APP_API}guest/sessions?eid=${eventID}`,
    token,
    { 
      enabled: !!eventID && !!landingPageData,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Fetch registration form data - Non-blocking (loads in background, deferred)
  const {
    data: registrationFormData,
    isLoading: isLoadingRegistrationForm,
  } = useCustomFecthQuery(
    ["eventForm", eventID],
    `${process.env.REACT_APP_API}event-form/${eventID}`,
    token,
    { 
      enabled: !!eventID && !!landingPageData,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Only block on critical data (landing page and event detail)
  // Secondary data (tickets, sessions, registration) loads in background
  if (isLoadingLandingPage || isLoadingGetMyEventDetail) {
    return <Loading title="Loading event..." size={160} fullScreen />;
  }

  // Get the first landing page data item (data is an array)
  const landingPage = landingPageData?.data?.[0];

  // Check if landing page exists
  const landingPageExists = landingPageData?.data && Array.isArray(landingPageData.data) && landingPageData.data.length > 0;
  const hasLandingPageError = landingPageError || (!isLoadingLandingPage && !landingPageExists);

  // Get event status from landing page data if available
  const eventStatus = landingPage?.eventData?.state || "Published";
  const eventType = landingPage?.eventData?.eventType;

  // In preview mode, always show the landing page regardless of publish status
  const shouldShowLandingPage = isPreview || eventStatus === "Published";

  // Check if custom landing page data exists
  const hasCustomLandingPage = landingPage?.templateId && landingPage?.eventData;

  // Debug logging
  console.log("Landing Page Data:", landingPageData);
  console.log("Has Custom Landing Page:", hasCustomLandingPage);
  console.log("Landing Page Error:", landingPageError);
  console.log("Landing Page Exists:", landingPageExists);

  // Show error if landing page data failed to load
  if (landingPageError && !isLoadingLandingPage) {
    console.error("Failed to load landing page data:", landingPageError);
  }

  // Error handling: Show error message if landing page doesn't exist
  if (hasLandingPageError && !isLoadingLandingPage) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: "600px",
            padding: "2rem",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              fontSize: "4rem",
              marginBottom: "1rem",
            }}
          >
            📄
          </div>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1rem",
              color: "#333",
            }}
          >
            Landing Page Not Found
          </h2>
          <p
            style={{
              fontSize: "1rem",
              color: "#666",
              marginBottom: "1.5rem",
              lineHeight: "1.6",
            }}
          >
            {landingPageError
              ? "We encountered an error while loading the landing page. Please try again later."
              : "The landing page for this event does not exist or has not been created yet."}
          </p>
          {eventID && (
            <p
              style={{
                fontSize: "0.875rem",
                color: "#999",
                fontFamily: "monospace",
              }}
            >
              Event ID: {eventID}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        style={{
          width: "100%",
          position: "relative",
          background:
            !isPreview && eventStatus === "Not Published" ? "#f68a20" : "transparent",
        }}
      >
        {!isPreview && eventStatus === "Not Published" ? (
          <div
            className="w-100"
            style={{ position: "relative", height: "80vh" }}
          >
            <LoaderComingSoon />
          </div>
        ) : null}
        {shouldShowLandingPage ? (
          <div
            className="w-100"
            style={{ position: "relative", minHeight: "100vh" }}
          >
            {hasCustomLandingPage && landingPage ? (
              <CustomLandingPageWithMemo
                landingPage={landingPage}
                getMyEventDetail={getMyEventDetail}
                ticketSettingsData={ticketSettingsData}
                sessionsData={sessionsData}
                registrationFormData={registrationFormData}
                eventID={eventID}
                params={params}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  minHeight: "100vh",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2rem",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    maxWidth: "600px",
                    padding: "2rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "4rem",
                      marginBottom: "1rem",
                    }}
                  >
                    🎨
                  </div>
                  <h2
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      marginBottom: "1rem",
                      color: "#333",
                    }}
                  >
                    Landing Page Not Configured
                  </h2>
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "#666",
                      marginBottom: "1.5rem",
                      lineHeight: "1.6",
                    }}
                  >
                    This event does not have a custom landing page configured yet. Please contact the event organizer for more information.
                  </p>
                  {eventID && (
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "#999",
                        fontFamily: "monospace",
                      }}
                    >
                      Event ID: {eventID}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}
        <div className="w-100">
          <CustomFooter />
        </div>
      </div>
    </React.Fragment>
  );
};

export default EventLandingCustomPublic;
